/**
 * Suministros: lo que cuesta la luz, el agua, el gas, internet y la limpieza de cada piso, y lo
 * que hay que devolver o cobrar a cada inquilino cada cuatrimestre.
 *
 * DOS PANTALLAS, COMO EN PROPIEDADES E INGRESOS Y GASTOS:
 *
 *  · El HUB (`/suministros`): una tarjeta por piso, con su foto de portada, su nombre y cómo va
 *    el cuatrimestre —«A devolver 2,20 €», «A pagar 15,00 €»—. Se ve de un vistazo qué piso
 *    pide atención sin entrar en ninguno.
 *  · El PISO (`/suministros/:id`), que responde en orden a lo que se pregunta:
 *     1. ¿CÓMO HA QUEDADO? El resumen del cuatrimestre, como la hoja de cálculo de siempre: por
 *        habitación, los días de estancia (que se pueden corregir a mano), lo entregado, lo
 *        gastado y la regularización, con los totales.
 *     2. ¿QUÉ ENTREGÓ CADA UNO, MES A MES? Como la hoja «Entregado», pero sacado de la
 *        conciliación de Ingresos y gastos: lo que cada inquilino pagó cada mes además de la renta
 *        (lo cobrado «a cuenta»). Un mes sin extracto todavía enseña el adelanto del contrato, en
 *        gris, como previsión. Cada mes se puede corregir a mano; la fila suma lo entregado del
 *        resumen.
 *     3. ¿QUÉ FACTURAS SON Y QUIÉN PAGA CADA UNA? Cada factura con el periodo que cobra y lo que
 *        le toca a cada inquilino de ella.
 *     4. ¿DE DÓNDE SALE EL GASTO? Los cuatro meses por suministro, de las facturas subidas en
 *        Ingresos y gastos o aquí mismo: no se teclea nada.
 *
 * SE SUBEN AQUÍ TAMBIÉN. Las facturas de luz, agua o wifi soltadas en esta pantalla van al mismo
 * sitio que las de Ingresos y gastos —un apunte del piso en el mes de su fecha—, así que entran
 * en la conciliación de ese mes como cualquier otra.
 *
 * EL REPARTO ES POR DÍAS Y POR FACTURA: cada día del periodo que cobra una factura se reparte
 * entre quienes vivían en el piso ese día. Cuando cambia el inquilino de una habitación, la luz
 * de julio que llega en septiembre la paga quien vivía en julio. Los días sin nadie no se
 * reparten: los asume la propiedad.
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  UtilitiesResponse,
  UtilityBill,
  UtilityCategory,
  UtilityDeliveredSource,
  UtilityMonth,
  UtilityPeriod,
  UtilitySettlementRow,
} from '@naar/shared';
import { MONTH_NAMES_ES } from '@naar/shared';
import { ApiError, api, queryKeys } from '../api/client';
import { useCan } from '../auth/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { Skeleton } from '../components/Skeleton';
import { Icon } from '../components/Icon';
import { toast } from '../components/feedback/Toaster';
import { ViewDocumentButton } from '../components/overlay/DocumentViewer';
import { formatEurCents } from '../lib/format';
import { usePropertyCovers } from '../lib/usePropertyCovers';
import { LEDGER_ACCEPT, useLedgerUpload } from './ledger/useLedgerUpload';
import styles from './UtilitiesPage.module.css';

const CATEGORIAS: { id: UtilityCategory; label: string }[] = [
  { id: 'luz', label: 'Luz' },
  { id: 'agua', label: 'Agua' },
  { id: 'gas', label: 'Gas' },
  { id: 'internet', label: 'Internet' },
  { id: 'limpieza', label: 'Limpieza' },
  { id: 'otros', label: 'Otros' },
];

const ESTADO: Record<UtilityPeriod['status'], { label: string; clase: string }> = {
  cerrado: { label: 'Terminado', clase: styles.statusClosed ?? '' },
  en_curso: { label: 'En curso', clase: styles.statusCurrent ?? '' },
  proximo: { label: 'Próximo', clase: styles.statusNext ?? '' },
};

/** «Ene – Abr», para la pestaña y la tarjeta. */
function rangoCorto(periodo: UtilityPeriod): string {
  const desde = Number(periodo.from.slice(5, 7)) - 1;
  const corto = (mes: number) => {
    const nombre = (MONTH_NAMES_ES[mes] ?? '').slice(0, 3);
    return nombre.charAt(0).toUpperCase() + nombre.slice(1);
  };
  return `${corto(desde)} – ${corto(desde + 3)}`;
}

function diasDelPeriodo(periodo: UtilityPeriod): number {
  const desde = new Date(`${periodo.from}T00:00:00Z`).getTime();
  const hasta = new Date(`${periodo.to}T00:00:00Z`).getTime();
  return Math.round((hasta - desde) / 86_400_000) + 1;
}

/**
 * El cuatrimestre que se enseña por defecto: el último que ya terminó CON INQUILINOS (es el que
 * hay que regularizar); si no hay ninguno, el que está en curso.
 */
function periodoPorDefecto(data: UtilitiesResponse): UtilityPeriod | null {
  const terminados = data.periods.filter((p) => p.status === 'cerrado' && p.rows.length > 0);
  return terminados.at(-1) ?? data.periods.find((p) => p.status === 'en_curso') ?? data.periods[0] ?? null;
}

/** «A devolver 2,20 €» (se le devuelve al inquilino) o «A pagar 15,00 €» (paga él). */
function textoResultado(balance: number): string {
  if (balance === 0) return 'Cuadra';
  return balance > 0 ? `A devolver ${formatEurCents(balance)}` : `A pagar ${formatEurCents(Math.abs(balance))}`;
}

// ===========================================================================
// El hub: una tarjeta por piso
// ===========================================================================

export function UtilitiesPage() {
  const year = new Date().getFullYear();
  /*
   * La lista de pisos sale de la propia respuesta de Suministros (la trae siempre). Después, una
   * consulta por piso para saber cómo va su cuatrimestre: son las mismas que abre la pantalla de
   * cada piso, así que al entrar ya están en caché.
   */
  const base = useQuery({
    queryKey: queryKeys.utilities({ year }),
    queryFn: () => api.utilities({ year }),
  });
  const pisos = base.data?.properties ?? [];
  const consultas = useQueries({
    queries: pisos.map((piso) => {
      const params = { propertyId: piso.id, year };
      return { queryKey: queryKeys.utilities(params), queryFn: () => api.utilities(params) };
    }),
  });
  const portadas = usePropertyCovers();

  const header = (
    <PageHeader
      title="Suministros"
      subtitle="Cada cuatrimestre se reparte el gasto de luz, agua, internet… entre los inquilinos y se compara con lo que adelantaron. Elige un piso."
    />
  );

  if (base.error) {
    return (
      <section>
        {header}
        <ErrorState error={base.error} onRetry={() => void base.refetch()} />
      </section>
    );
  }

  return (
    <section>
      {header}
      {base.isPending ? (
        <div className={styles.hub}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={280} radius={14} />
          ))}
        </div>
      ) : pisos.length === 0 ? (
        <EmptyState
          icon="building"
          title="Todavía no hay inmuebles"
          body="Da de alta un inmueble en Propiedades; aquí verás sus suministros y la regularización de sus inquilinos."
        />
      ) : (
        <div className={styles.hub}>
          {pisos.map((piso, index) => (
            <HubCard
              key={piso.id}
              id={piso.id}
              name={piso.name}
              city={piso.city}
              cover={portadas.get(piso.id)?.url ?? null}
              placeholderColor={portadas.get(piso.id)?.placeholderColor ?? null}
              data={consultas[index]?.data}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function HubCard({
  id,
  name,
  city,
  cover,
  placeholderColor,
  data,
}: {
  id: string;
  name: string;
  city: string;
  cover: string | null;
  placeholderColor: string | null;
  data: UtilitiesResponse | undefined;
}) {
  const periodo = data ? periodoPorDefecto(data) : null;
  const balance = periodo ? Number(periodo.balance) : 0;
  const sinInquilinos = Boolean(periodo && periodo.rows.length === 0);
  const tono =
    !periodo || sinInquilinos || periodo.status === 'proximo'
      ? styles.hubNeutral
      : balance > 0
        ? styles.hubReturn
        : balance < 0
          ? styles.hubCharge
          : styles.hubNeutral;

  return (
    <Link to={`/suministros/${id}`} className={styles.hubCard}>
      <span
        className={styles.hubPhoto}
        style={{
          ...(cover ? { backgroundImage: `url("${cover}")` } : {}),
          ...(placeholderColor ? { backgroundColor: placeholderColor } : {}),
        }}
        aria-hidden="true"
      >
        {!cover && <Icon name="building" size={28} strokeWidth={1.6} />}
      </span>
      <span className={styles.hubBody}>
        <span className={styles.hubName}>{name}</span>
        <span className={styles.hubCity}>{city}</span>
        <span className={styles.hubFoot}>
          {periodo ? (
            <>
              <span className={styles.hubPeriod}>
                <span className={`${styles.statusDot} ${ESTADO[periodo.status].clase}`} aria-hidden="true" />
                {rangoCorto(periodo)} · {ESTADO[periodo.status].label}
              </span>
              <span className={`${styles.hubResult} ${tono}`}>
                {periodo.status === 'proximo'
                  ? 'Sin empezar'
                  : sinInquilinos
                    ? 'Sin inquilinos'
                    : textoResultado(balance)}
              </span>
            </>
          ) : (
            <span className={styles.hubPeriod}>Calculando…</span>
          )}
        </span>
        {periodo && !sinInquilinos && periodo.status !== 'proximo' && (
          <span className={styles.hubMeta}>
            Gasto {formatEurCents(periodo.total)} · Entregado {formatEurCents(periodo.delivered)}
          </span>
        )}
      </span>
    </Link>
  );
}

// ===========================================================================
// Un piso
// ===========================================================================

export function UtilityDetailPage() {
  const { propertyId = '' } = useParams();
  const navigate = useNavigate();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [periodIndex, setPeriodIndex] = useState<number | null>(null);

  const params = { propertyId, year };
  const { data, isPending, error, refetch } = useQuery({
    queryKey: queryKeys.utilities(params),
    queryFn: () => api.utilities(params),
  });

  useEffect(() => {
    if (!data) return;
    setPeriodIndex((current) =>
      current && data.periods.some((p) => p.index === current) ? current : (periodoPorDefecto(data)?.index ?? null),
    );
  }, [data]);

  const periodo = data?.periods.find((p) => p.index === periodIndex) ?? null;
  const mesesBloque = useMemo(() => {
    if (!periodo || !data) return [];
    const desde = Number(periodo.from.slice(5, 7)) - 1;
    return [desde, desde + 1, desde + 2, desde + 3].flatMap((mes) => {
      const month = data.months[mes];
      return month ? [{ mes, month }] : [];
    });
  }, [periodo, data]);

  const cambiarAnio = (delta: number) => {
    setYear((y) => y + delta);
    setPeriodIndex(null);
  };

  const controles = (
    <div className={styles.yearPicker} role="group" aria-label="Año">
      <button type="button" className={styles.yearArrow} onClick={() => cambiarAnio(-1)} aria-label="Año anterior">
        <Icon name="chevron-left" size={16} strokeWidth={2.2} />
      </button>
      <span className={styles.year}>{year}</span>
      <button
        type="button"
        className={styles.yearArrow}
        onClick={() => cambiarAnio(1)}
        aria-label="Año siguiente"
        disabled={year >= new Date().getFullYear()}
      >
        <Icon name="chevron-right" size={16} strokeWidth={2.2} />
      </button>
    </div>
  );

  const header = (
    <PageHeader
      title={data?.property?.name ?? 'Suministros'}
      subtitle={
        data?.property
          ? `${data.property.city} · cada factura repartida entre los inquilinos que vivían en el piso los días que cobra.`
          : undefined
      }
      backTo="/suministros"
      backLabel="Suministros"
      actions={controles}
    />
  );

  if (error) {
    return (
      <section>
        {header}
        <ErrorState error={error} onRetry={() => void refetch()} />
      </section>
    );
  }

  return (
    <section>
      {header}

      {isPending || !data ? (
        <>
          <Skeleton height={64} radius={8} />
          <div style={{ height: 16 }} />
          <Skeleton height={260} radius={8} />
          <div style={{ height: 16 }} />
          <Skeleton height={280} radius={8} />
        </>
      ) : !data.property ? (
        <EmptyState
          icon="building"
          title="Este piso ya no existe"
          body="Puede que se haya dado de baja. Vuelve a Suministros y elige otro."
          actionLabel="Volver a Suministros"
          onAction={() => navigate('/suministros')}
        />
      ) : (
        <>
          <UploadBar property={data.property} />

          {/* --- Pestañas: los tres cuatrimestres ---------------------------------- */}
          <div className={styles.tabs} role="tablist" aria-label="Cuatrimestre">
            {data.periods.map((p) => {
              const activo = p.index === periodIndex;
              return (
                <button
                  key={p.index}
                  type="button"
                  role="tab"
                  aria-selected={activo}
                  className={activo ? `${styles.tab} ${styles.tabActive}` : styles.tab}
                  onClick={() => setPeriodIndex(p.index)}
                >
                  <span className={styles.tabTop}>
                    <span className={styles.tabRange}>{rangoCorto(p)}</span>
                    <span className={`${styles.statusDot} ${ESTADO[p.status].clase}`} aria-hidden="true" />
                  </span>
                  <span className={styles.tabMeta}>
                    <span>{ESTADO[p.status].label}</span>
                    {p.status !== 'proximo' && <span className={styles.tabTotal}>{formatEurCents(p.total)}</span>}
                  </span>
                </button>
              );
            })}
          </div>

          {periodo && (
            <>
              <Summary periodo={periodo} year={data.year} />
              <MonthlyShares periodo={periodo} meses={mesesBloque} year={data.year} />
              <Bills periodo={periodo} />
              <ExpenseDetail periodo={periodo} meses={mesesBloque} />
            </>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// 1. El resumen del cuatrimestre
// ---------------------------------------------------------------------------

function Summary({ periodo, year }: { periodo: UtilityPeriod; year: number }) {
  const balance = Number(periodo.balance);
  const tono = balance > 0 ? styles.toneReturn : balance < 0 ? styles.toneCharge : styles.toneEven;
  const titulo = (
    <div className={styles.resultHead}>
      <span className={styles.resultTitle}>
        {periodo.label} {year}
      </span>
      <span className={`${styles.status} ${ESTADO[periodo.status].clase}`}>
        {periodo.status === 'en_curso' ? 'En curso · provisional' : ESTADO[periodo.status].label}
      </span>
    </div>
  );

  if (periodo.status === 'proximo') {
    return (
      <div className={styles.result}>
        {titulo}
        <p className={styles.resultSentence}>Este cuatrimestre todavía no ha empezado.</p>
      </div>
    );
  }

  if (periodo.rows.length === 0) {
    return (
      <div className={styles.result}>
        {titulo}
        <p className={styles.resultSentence}>Nadie tuvo contrato en este piso durante este cuatrimestre.</p>
      </div>
    );
  }

  const diasPeriodo = diasDelPeriodo(periodo);

  return (
    <div className={styles.result}>
      {titulo}

      {/* Las dos cifras de cabecera de la hoja: lo gastado y cómo queda. */}
      <div className={styles.summaryTop}>
        <div className={styles.summaryFigure}>
          <span className={styles.summaryLabel}>Gasto del cuatrimestre</span>
          <span className={styles.summaryValue}>{formatEurCents(periodo.total)}</span>
        </div>
        <div className={styles.summaryFigure}>
          <span className={styles.summaryLabel}>
            {balance > 0 ? 'A devolver a los inquilinos' : balance < 0 ? 'A pagar por los inquilinos' : 'Regularización'}
          </span>
          <span className={`${styles.summaryValue} ${tono}`}>
            {balance === 0 ? formatEurCents(0) : formatEurCents(Math.abs(balance))}
          </span>
        </div>
        {Number(periodo.unassigned) > 0 && (
          <div className={styles.summaryFigure}>
            <span className={styles.summaryLabel}>Días sin inquilinos · lo asume la propiedad</span>
            <span className={`${styles.summaryValue} ${styles.summaryValueMuted}`}>
              {formatEurCents(periodo.unassigned)}
            </span>
          </div>
        )}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.summary}>
          <thead>
            <tr>
              <th className={styles.left}>Hab.</th>
              <th className={styles.left}>Inquilino</th>
              <th>Días de estancia</th>
              <th>Entregado</th>
              <th>Gasto</th>
              <th>Regularización</th>
            </tr>
          </thead>
          <tbody>
            {periodo.rows.map((row) => (
              <SummaryRow key={row.leaseId} row={row} year={year} period={periodo.index} diasPeriodo={diasPeriodo} />
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th className={`${styles.left} ${styles.sRoom}`} colSpan={2} scope="row">
                Total
              </th>
              {/* Los días totales no se enseñan: no dicen nada por sí solos. */}
              <td className={styles.sDays} />
              <td className={styles.sDel} data-label="Entregado">
                {formatEurCents(periodo.delivered)}
              </td>
              <td className={styles.sShare} data-label="Gasto">
                {formatEurCents(Number(periodo.total) - Number(periodo.unassigned))}
              </td>
              <td className={styles.sRes}>
                <ResultBadge balance={balance} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className={styles.summaryNote}>
        Lo entregado sale de la conciliación de Ingresos y gastos (lo pagado además de la renta; sin extracto, el adelanto
        del contrato). El gasto, de repartir cada factura por los días que cobra
        entre quienes vivían en el piso esos días: quien se fue no paga lo de después, ni quien entró lo de antes. Si
        alguien no estuvo todos los días que dice su contrato, corrígelos en su fila: cuenta en proporción.
      </p>
    </div>
  );
}

function ResultBadge({ balance }: { balance: number }) {
  if (balance === 0) return <span className={styles.badge}>{formatEurCents(0)}</span>;
  return balance > 0 ? (
    <span className={`${styles.badge} ${styles.badgeReturn}`}>A devolver {formatEurCents(balance)}</span>
  ) : (
    <span className={`${styles.badge} ${styles.badgeCharge}`}>A pagar {formatEurCents(Math.abs(balance))}</span>
  );
}

function SummaryRow({
  row,
  year,
  period,
  diasPeriodo,
}: {
  row: UtilitySettlementRow;
  year: number;
  period: number;
  diasPeriodo: number;
}) {
  return (
    <tr>
      <td className={`${styles.left} ${styles.sRoom}`}>
        <span className={styles.room}>{row.unitCode}</span>
      </td>
      <td className={`${styles.left} ${styles.sName}`}>
        <span className={styles.person}>{row.tenant?.fullName ?? 'Sin inquilino'}</span>
        {/* En el teléfono la columna de habitación se esconde: va aquí, debajo del nombre. */}
        <span className={styles.roomMobile}>{row.unitCode}</span>
      </td>
      <td className={styles.sDays} data-label="Días">
        <DaysCell row={row} year={year} period={period} diasPeriodo={diasPeriodo} />
      </td>
      <td className={styles.sDel} data-label="Entregado">
        {formatEurCents(row.delivered)}
      </td>
      <td className={styles.sShare} data-label="Gasto">
        {formatEurCents(row.share)}
      </td>
      <td className={styles.sRes}>
        <ResultBadge balance={Number(row.balance)} />
      </td>
    </tr>
  );
}

/**
 * Los días de estancia, editables ahí mismo: se pulsa, se escribe y Enter (o salir del campo)
 * guarda. Si se corrigieron, debajo se recuerda cuántos dice el contrato y se puede volver a ellos.
 */
function DaysCell({
  row,
  year,
  period,
  diasPeriodo,
}: {
  row: UtilitySettlementRow;
  year: number;
  period: number;
  diasPeriodo: number;
}) {
  const queryClient = useQueryClient();
  const canEdit = useCan('lease:update');
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState(String(row.days));

  useEffect(() => setValor(String(row.days)), [row.days]);

  const guardar = useMutation({
    mutationFn: (days: number | null) => api.setUtilityStay({ leaseId: row.leaseId, year, period, days }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['utilities'] });
      setEditando(false);
    },
    onError: (fault: unknown) =>
      toast.error(fault instanceof ApiError ? fault.message : 'No se han podido guardar los días.'),
  });

  function confirmar() {
    const dias = Number(valor);
    if (!Number.isInteger(dias) || dias < 0 || dias > diasPeriodo) {
      toast.error(`Los días tienen que ir de 0 a ${diasPeriodo}.`);
      return;
    }
    if (dias === row.days) {
      setEditando(false);
      return;
    }
    // Escribir los mismos días que el contrato es volver a los del contrato.
    guardar.mutate(dias === row.computedDays ? null : dias);
  }

  if (editando) {
    return (
      <span className={styles.daysEdit}>
        <input
          type="number"
          inputMode="numeric"
          min={0}
          max={diasPeriodo}
          className={styles.daysInput}
          value={valor}
          autoFocus
          disabled={guardar.isPending}
          aria-label={`Días de estancia de ${row.tenant?.fullName ?? row.unitCode}`}
          onChange={(event) => setValor(event.target.value)}
          onBlur={confirmar}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              confirmar();
            }
            if (event.key === 'Escape') {
              setValor(String(row.days));
              setEditando(false);
            }
          }}
        />
      </span>
    );
  }

  return (
    <span className={styles.daysCell}>
      {canEdit ? (
        <button
          type="button"
          className={styles.editable}
          onClick={() => setEditando(true)}
          title="Corregir los días de estancia"
        >
          {row.days}
          <Icon name="edit" size={12} strokeWidth={2} />
        </button>
      ) : (
        row.days
      )}
      {row.daysEdited && (
        <span className={styles.daysNote}>
          Contrato: {row.computedDays}
          {canEdit && (
            <button
              type="button"
              className={styles.daysReset}
              onClick={() => guardar.mutate(null)}
              disabled={guardar.isPending}
              title="Volver a los días del contrato"
            >
              Restablecer
            </button>
          )}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// 2. Lo que entregó cada inquilino, mes a mes
// ---------------------------------------------------------------------------

/**
 * Lo adelantado por cada habitación en cada mes, como la hoja «Entregado»: 50 € al mes los meses
 * enteros y la parte de los días los que no. La fila suma lo «Entregado» del resumen y la última
 * fila, lo de todas las habitaciones cada mes. El adelanto mensual de cada contrato se corrige aquí.
 */
function MonthlyShares({
  periodo,
  meses,
  year,
}: {
  periodo: UtilityPeriod;
  meses: { mes: number; month: UtilityMonth }[];
  year: number;
}) {
  if (periodo.status === 'proximo' || periodo.rows.length === 0) return null;
  const totalesMes = meses.map((_, i) =>
    periodo.rows.reduce((suma, row) => suma + Number(row.deliveredByMonth?.[i] ?? 0), 0),
  );

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <h2 className={styles.cardTitle}>Entregado, mes a mes</h2>
        <span className={styles.cardHint}>
          Sale de la conciliación de Ingresos y gastos: lo que cada inquilino pagó cada mes además de su renta (lo que
          queda cobrado a cuenta, normalmente los 50 € de suministros). Los meses que aún no tienen extracto enseñan, en
          gris, el adelanto del contrato. Pulsa un mes para corregirlo a mano; vacío vuelve a lo de la conciliación.
        </span>
      </div>

      <div className={styles.tableWrap}>
        <table className={`${styles.block} ${styles.monthly}`}>
          <colgroup>
            <col className={styles.colLabel} />
            <col className={styles.colAdvance} />
            {meses.map(({ mes }) => (
              <col key={mes} />
            ))}
            <col className={styles.colTotal} />
          </colgroup>
          <thead>
            <tr>
              <th className={styles.blockLabel} scope="col">
                Habitación
              </th>
              <th scope="col">Adelanto/mes</th>
              {meses.map(({ mes }) => (
                <th key={mes} scope="col">
                  <span className={styles.monthFull}>{MONTH_NAMES_ES[mes]}</span>
                  <span className={styles.monthShort}>{MONTH_NAMES_ES[mes]?.slice(0, 3)}</span>
                </th>
              ))}
              <th className={styles.blockTotal} scope="col">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {periodo.rows.map((row) => (
              <tr key={row.leaseId}>
                <th className={styles.blockLabel} scope="row">
                  <span className={styles.person}>{row.unitCode}</span>
                  <span className={styles.room}>{row.tenant?.fullName ?? 'Sin inquilino'}</span>
                </th>
                <td>
                  <AdvanceCell row={row} />
                </td>
                {meses.map(({ mes }, i) => (
                  <td key={mes}>
                    <DeliveredCell
                      row={row}
                      month={`${year}-${String(mes + 1).padStart(2, '0')}`}
                      amount={row.deliveredByMonth?.[i] ?? '0.00'}
                      source={row.deliveredSources?.[i] ?? 'contrato'}
                    />
                  </td>
                ))}
                <td className={styles.blockTotal}>{formatEurCents(row.delivered)}</td>
              </tr>
            ))}
            <tr className={styles.blockSum}>
              <th className={styles.blockLabel} scope="row">
                Total
              </th>
              <td />
              {meses.map(({ mes }, i) => (
                <td key={mes}>{formatEurCents(totalesMes[i] ?? 0)}</td>
              ))}
              <td className={styles.blockTotal}>{formatEurCents(periodo.delivered)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * El adelanto mensual de suministros del contrato, editable ahí mismo. Igual que los días: se
 * pulsa, se escribe («45» o «45,50») y Enter (o salir del campo) guarda; Esc deja lo que había.
 * Un campo pequeño y propio, no el de importes de los formularios: en una columna de tabla, ese
 * con su sufijo y sus dos botones se quedaba en 50px y no se veía la cifra.
 */
function AdvanceCell({ row }: { row: UtilitySettlementRow }) {
  const queryClient = useQueryClient();
  const canEdit = useCan('lease:update');
  const [editando, setEditando] = useState(false);
  const aTexto = (importe: string) => Number(importe).toFixed(2).replace('.', ',');
  const [valor, setValor] = useState(() => aTexto(row.monthlyAdvance));

  useEffect(() => setValor(aTexto(row.monthlyAdvance)), [row.monthlyAdvance]);

  const guardar = useMutation({
    mutationFn: (importe: string) => api.updateLease(row.leaseId, { utilityAdvance: importe }, row.leaseUpdatedAt),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['utilities'] });
      toast.success('Adelanto de suministros actualizado.');
      setEditando(false);
    },
    onError: (fault: unknown) =>
      toast.error(fault instanceof ApiError ? fault.message : 'No se ha podido guardar el adelanto.'),
  });

  function confirmar() {
    // Con coma, la coma es el decimal y los puntos son miles («1.000,50»); sin coma, el punto es
    // el decimal («45.50»), que es como lo escribe quien viene de una hoja en inglés.
    const sinEspacios = valor.trim().replace(/\s|€/g, '');
    const limpio = sinEspacios.includes(',') ? sinEspacios.replace(/\./g, '').replace(',', '.') : sinEspacios;
    const importe = Number(limpio);
    if (limpio === '' || !Number.isFinite(importe) || importe < 0 || importe > 10_000) {
      toast.error('Escribe un importe válido, por ejemplo 50 o 45,50.');
      return;
    }
    const nuevo = importe.toFixed(2);
    if (Number(nuevo) === Number(row.monthlyAdvance)) {
      setEditando(false);
      return;
    }
    guardar.mutate(nuevo);
  }

  if (editando) {
    return (
      <span className={styles.advanceEdit}>
        <input
          type="text"
          inputMode="decimal"
          className={styles.advanceInput}
          value={valor}
          autoFocus
          disabled={guardar.isPending}
          aria-label={`Adelanto mensual de suministros de ${row.tenant?.fullName ?? row.unitCode}`}
          onFocus={(event) => event.target.select()}
          onChange={(event) => setValor(event.target.value)}
          onBlur={confirmar}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              confirmar();
            }
            if (event.key === 'Escape') {
              setValor(aTexto(row.monthlyAdvance));
              setEditando(false);
            }
          }}
        />
        <span className={styles.advanceSuffix} aria-hidden="true">
          €
        </span>
      </span>
    );
  }

  return canEdit ? (
    <button type="button" className={styles.editable} onClick={() => setEditando(true)} title="Cambiar el adelanto">
      {formatEurCents(row.monthlyAdvance)}
      <Icon name="edit" size={12} strokeWidth={2} />
    </button>
  ) : (
    <>{formatEurCents(row.monthlyAdvance)}</>
  );
}

const ORIGEN_ENTREGADO: Record<UtilityDeliveredSource, string> = {
  banco: 'De la conciliación: lo que pagó además de la renta este mes.',
  manual: 'Corregido a mano. Vacíalo para volver a lo de la conciliación.',
  contrato: 'Previsto: este mes aún no tiene extracto del banco, así que es el adelanto del contrato.',
};

/**
 * Lo entregado por un contrato en un mes, editable ahí mismo como el adelanto: se pulsa, se
 * escribe y Enter (o salir del campo) guarda; vacío vuelve a lo que diga la conciliación; Esc
 * deja lo que había. El origen se ve en el estilo: normal lo del banco, con punto lo corregido
 * a mano y en gris lo previsto.
 */
function DeliveredCell({
  row,
  month,
  amount,
  source,
}: {
  row: UtilitySettlementRow;
  month: string;
  amount: string;
  source: UtilityDeliveredSource;
}) {
  const queryClient = useQueryClient();
  const canEdit = useCan('lease:update');
  const [editando, setEditando] = useState(false);
  const aTexto = (importe: string) => Number(importe).toFixed(2).replace('.', ',');
  const [valor, setValor] = useState(() => aTexto(amount));

  useEffect(() => setValor(aTexto(amount)), [amount]);

  const guardar = useMutation({
    mutationFn: (importe: string | null) => api.setUtilityDelivered({ leaseId: row.leaseId, month, amount: importe }),
    onSuccess: async (_, importe) => {
      await queryClient.invalidateQueries({ queryKey: ['utilities'] });
      toast.success(importe === null ? 'Vuelve a lo que dice la conciliación.' : 'Entregado corregido.');
      setEditando(false);
    },
    onError: (fault: unknown) =>
      toast.error(fault instanceof ApiError ? fault.message : 'No se ha podido guardar lo entregado.'),
  });

  function confirmar() {
    const sinEspacios = valor.trim().replace(/\s|€/g, '');
    if (sinEspacios === '') {
      if (source === 'manual') guardar.mutate(null);
      else setEditando(false);
      return;
    }
    const limpio = sinEspacios.includes(',') ? sinEspacios.replace(/\./g, '').replace(',', '.') : sinEspacios;
    const importe = Number(limpio);
    if (!Number.isFinite(importe) || importe < 0 || importe > 10_000) {
      toast.error('Escribe un importe válido, por ejemplo 50 o 45,50.');
      return;
    }
    const nuevo = importe.toFixed(2);
    if (Number(nuevo) === Number(amount) && source === 'manual') {
      setEditando(false);
      return;
    }
    guardar.mutate(nuevo);
  }

  if (editando) {
    return (
      <span className={styles.advanceEdit}>
        <input
          type="text"
          inputMode="decimal"
          className={styles.advanceInput}
          value={valor}
          autoFocus
          disabled={guardar.isPending}
          aria-label={`Entregado en ${month} por ${row.tenant?.fullName ?? row.unitCode}`}
          onFocus={(event) => event.target.select()}
          onChange={(event) => setValor(event.target.value)}
          onBlur={confirmar}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              confirmar();
            }
            if (event.key === 'Escape') {
              setValor(aTexto(amount));
              setEditando(false);
            }
          }}
        />
        <span className={styles.advanceSuffix} aria-hidden="true">
          €
        </span>
      </span>
    );
  }

  const cifra =
    Number(amount) === 0 && source !== 'manual' ? <span className={styles.nil}>—</span> : formatEurCents(amount);
  const clase = [
    styles.delivered,
    source === 'contrato' ? styles.deliveredPlanned : '',
    source === 'manual' ? styles.deliveredManual : '',
  ]
    .filter(Boolean)
    .join(' ');

  return canEdit ? (
    <button
      type="button"
      className={`${styles.editable} ${clase}`}
      onClick={() => setEditando(true)}
      title={`${ORIGEN_ENTREGADO[source]} Pulsa para corregirlo.`}
    >
      {cifra}
    </button>
  ) : (
    <span className={clase} title={ORIGEN_ENTREGADO[source]}>
      {cifra}
    </span>
  );
}

// ---------------------------------------------------------------------------
// 3. De dónde sale el gasto
// ---------------------------------------------------------------------------

function ExpenseDetail({ periodo, meses }: { periodo: UtilityPeriod; meses: { mes: number; month: UtilityMonth }[] }) {
  if (periodo.status === 'proximo') return null;
  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <h2 className={styles.cardTitle}>De dónde sale el gasto</h2>
        <span className={styles.cardHint}>
          {Number(periodo.total) === 0
            ? 'Todavía no hay facturas de suministros en este cuatrimestre: súbelas aquí arriba o en Ingresos y gastos.'
            : 'Las facturas de suministros, cada una repartida por los días que cobra en cada mes.'}
        </span>
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.block}>
          <colgroup>
            <col className={styles.colLabel} />
            {meses.map(({ mes }) => (
              <col key={mes} />
            ))}
            <col className={styles.colTotal} />
          </colgroup>
          <thead>
            <tr>
              <th className={styles.blockLabel} scope="col">
                Suministro
              </th>
              {meses.map(({ mes }) => (
                <th key={mes} scope="col">
                  {/* En el teléfono, «sep», «oct»…: los nombres enteros se pisaban. */}
                  <span className={styles.monthFull}>{MONTH_NAMES_ES[mes]}</span>
                  <span className={styles.monthShort}>{MONTH_NAMES_ES[mes]?.slice(0, 3)}</span>
                </th>
              ))}
              <th className={styles.blockTotal} scope="col">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {CATEGORIAS.map((categoria) => {
              const totalCategoria = meses.reduce((sum, { month }) => sum + Number(month.amounts[categoria.id]), 0);
              return (
                <tr key={categoria.id} className={totalCategoria === 0 ? styles.blockEmptyRow : undefined}>
                  <th className={styles.blockLabel} scope="row">
                    <span className={`${styles.swatch} ${styles[`swatch_${categoria.id}`] ?? ''}`} aria-hidden="true" />
                    {categoria.label}
                  </th>
                  {meses.map(({ mes, month }) => (
                    <td key={mes}>
                      {Number(month.amounts[categoria.id]) === 0 ? (
                        <span className={styles.nil}>—</span>
                      ) : (
                        formatEurCents(month.amounts[categoria.id])
                      )}
                    </td>
                  ))}
                  <td className={styles.blockTotal}>
                    {totalCategoria === 0 ? <span className={styles.nil}>—</span> : formatEurCents(totalCategoria)}
                  </td>
                </tr>
              );
            })}
            <tr className={styles.blockSum}>
              <th className={styles.blockLabel} scope="row">
                Total del mes
              </th>
              {meses.map(({ mes, month }) => (
                <td key={mes}>{formatEurCents(month.total)}</td>
              ))}
              <td className={styles.blockTotal}>{formatEurCents(periodo.total)}</td>
            </tr>
          </tbody>
          {/* La ocupación, aparte: no es dinero y mezclada con el gasto confundía. */}
          <tbody className={styles.blockOccupancy}>
            <tr>
              <th className={styles.blockLabel} scope="row">
                Personas
              </th>
              {meses.map(({ mes, month }) => (
                <td key={mes}>{month.people > 0 ? month.people : <span className={styles.nil}>—</span>}</td>
              ))}
              <td className={styles.blockTotal} />
            </tr>
            <tr>
              <th className={styles.blockLabel} scope="row">
                Coste por persona
              </th>
              {meses.map(({ mes, month }) => (
                <td key={mes}>
                  {month.costPerPerson && Number(month.costPerPerson) > 0 ? (
                    formatEurCents(month.costPerPerson)
                  ) : (
                    <span className={styles.nil}>—</span>
                  )}
                </td>
              ))}
              <td className={styles.blockTotal} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subir facturas desde aquí
// ---------------------------------------------------------------------------

/**
 * Soltar las facturas de luz, agua o wifi de este piso. Van a la cola de subida, igual que en
 * Ingresos y gastos: se apuntan en el piso y en el mes de su fecha y entran en la conciliación.
 */
function UploadBar({ property }: { property: { id: string; name: string } }) {
  const canManage = useCan('ledger:manage');
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const { subir, enCola } = useLedgerUpload(property);
  if (!canManage) return null;

  return (
    <div
      className={over ? `${styles.upload} ${styles.uploadOver}` : styles.upload}
      onDragOver={(event) => {
        if (!Array.from(event.dataTransfer.types).includes('Files')) return;
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        subir(Array.from(event.dataTransfer.files));
      }}
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={LEDGER_ACCEPT}
        className={styles.uploadInput}
        tabIndex={-1}
        aria-hidden="true"
        onChange={(event) => {
          subir(Array.from(event.target.files ?? []));
          event.target.value = '';
        }}
      />
      <button type="button" className={styles.uploadButton} onClick={() => inputRef.current?.click()}>
        <Icon name="upload" size={15} strokeWidth={2} />
        {enCola > 0 ? `${enCola} en cola · puedes soltar más` : 'Subir facturas de luz, agua o wifi'}
      </button>
      <span className={styles.uploadHint}>
        Arrástralas aquí o púlsalo. Se apuntan en {property.name}, en el mes de su fecha, y entran en la conciliación.
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Las facturas y quién paga cada una
// ---------------------------------------------------------------------------

/** «14/07/26 – 12/08/26». */
function rangoDeFechas(from: string, to: string): string {
  const corta = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}`;
  return `${corta(from)} – ${corta(to)}`;
}

function nombreMes(key: string): string {
  return `${MONTH_NAMES_ES[Number(key.slice(5, 7)) - 1]?.toLowerCase() ?? key} de ${key.slice(0, 4)}`;
}

function Bills({ periodo }: { periodo: UtilityPeriod }) {
  if (periodo.status === 'proximo' || periodo.bills.length === 0) return null;
  const quien = new Map(periodo.rows.map((row) => [row.leaseId, row]));

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <h2 className={styles.cardTitle}>Facturas y quién paga cada una</h2>
        <span className={styles.cardHint}>
          Cada factura se reparte por los días que cobra entre quienes vivían en el piso esos días. Corrige el piso o
          el periodo de una factura desde su apunte en la conciliación.
        </span>
      </div>
      <ul className={styles.bills}>
        {periodo.bills.map((bill) => (
          <BillRow key={bill.entryId} bill={bill} quien={quien} />
        ))}
      </ul>
    </div>
  );
}

function BillRow({ bill, quien }: { bill: UtilityBill; quien: Map<string, UtilitySettlementRow> }) {
  const parcial = bill.inPeriod !== bill.amount;
  return (
    <li className={styles.bill}>
      <div className={styles.billHead}>
        <span className={`${styles.swatch} ${styles[`swatch_${bill.category}`] ?? ''}`} aria-hidden="true" />
        <span className={styles.billName}>
          {bill.concept}
          {bill.vendorName && bill.vendorName !== bill.concept && (
            <span className={styles.billVendor}> · {bill.vendorName}</span>
          )}
        </span>
        {bill.draft && <span className={styles.billDraft}>Sin revisar</span>}
        <span className={styles.billAmount}>
          {formatEurCents(parcial ? bill.inPeriod : bill.amount)}
          {parcial && <span className={styles.billOf}> de {formatEurCents(bill.amount)}</span>}
        </span>
        {bill.document && <ViewDocumentButton document={{ id: bill.document.id, name: bill.document.name }} />}
      </div>
      <div className={styles.billMeta}>
        {bill.estimatedPeriod ? (
          <span className={styles.billEstimated}>
            Sin periodo en la factura: se reparte el mes de {nombreMes(bill.month)}.{' '}
            <Link to={`/ingresos-y-gastos/conciliacion?mes=${bill.month}`}>Corregirlo</Link>
          </span>
        ) : (
          <span>
            Cobra del {rangoDeFechas(bill.from, bill.to)}
            {parcial ? ' (una parte cae en otro cuatrimestre)' : ''} · apuntada en {nombreMes(bill.month)}
          </span>
        )}
      </div>
      <div className={styles.billShares}>
        {bill.shares.map((share) => {
          const row = quien.get(share.leaseId);
          return (
            <span key={share.leaseId} className={styles.billShare}>
              <span className={styles.billWho}>
                {row ? `${row.unitCode} · ${row.tenant?.fullName ?? 'Sin inquilino'}` : 'Contrato'}
              </span>
              <span className={styles.billPart}>{formatEurCents(share.amount)}</span>
            </span>
          );
        })}
        {Number(bill.unassigned) > 0 && (
          <span className={`${styles.billShare} ${styles.billShareEmpty}`}>
            <span className={styles.billWho}>Días sin nadie · propiedad</span>
            <span className={styles.billPart}>{formatEurCents(bill.unassigned)}</span>
          </span>
        )}
      </div>
    </li>
  );
}
